import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { AUTH } from "@/constants/testIds";
import BrandLogo from "@/components/BrandLogo";
import { unlockNarrator } from "@/lib/narrator";

const NAVY = "#0B0E1A";
const GOLD = "#C8A96E";
const PARCH = "#F4EFE6";

export default function Landing() {
  const { user, loginEmail, registerEmail } = useAuth();
  const [phase, setPhase] = useState("welcome"); // 'welcome' | 'auth'
  const [mode, setMode] = useState("register"); // begin defaults to register
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    if (user) {
      if (user.has_soul_record) navigate("/app/oracle", { replace: true });
      else navigate("/intake", { replace: true });
    }
  }, [user, navigate]);

  const beginJourney = () => {
    // Prime iOS/Safari Web Speech engine on first tap
    unlockNarrator();
    setPhase("auth");
  };

  const submit = async (e) => {
    e.preventDefault();
    unlockNarrator();
    setError("");
    setBusy(true);
    try {
      const u =
        mode === "login"
          ? await loginEmail(email, password)
          : await registerEmail(email, password, name || email.split("@")[0]);
      toast.success("The veil parts.");
      if (u.has_soul_record) navigate("/app/oracle");
      else navigate("/intake");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong.";
      setError(typeof msg === "string" ? msg : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  const startGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app/oracle";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div
      className="relative min-h-screen overflow-hidden"
      style={{ background: NAVY }}
    >
      <div className="starfield-subtle" />

      <AnimatePresence mode="wait">
        {phase === "welcome" ? (
          <WelcomeScreen key="welcome" onBegin={beginJourney} />
        ) : (
          <AuthScreen
            key="auth"
            mode={mode}
            setMode={setMode}
            email={email}
            setEmail={setEmail}
            password={password}
            setPassword={setPassword}
            name={name}
            setName={setName}
            error={error}
            busy={busy}
            submit={submit}
            startGoogle={startGoogle}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// WELCOME SCREEN — the first thing anyone sees
// ─────────────────────────────────────────────────────────────
function WelcomeScreen({ onBegin }) {
  return (
    <motion.div
      key="welcome"
      data-testid={AUTH.welcomeScreen}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5, ease: "easeInOut" }}
      className="relative min-h-screen flex flex-col items-center justify-center px-6 py-14"
    >
      {/* Soul Mark artwork — lands first */}
      <motion.img
        src="/houses/water.png"
        alt="Soul Mark"
        aria-hidden
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.2, ease: "easeInOut" }}
        className="mb-8"
        style={{
          width: 170,
          height: 170,
          objectFit: "cover",
          borderRadius: 18,
          border: `1px solid ${GOLD}55`,
          boxShadow:
            "0 0 32px rgba(200,169,110,0.22), 0 24px 60px rgba(0,0,0,0.55)",
        }}
      />

      {/* Wordmark */}
      <motion.h1
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
        className="font-heading font-light text-center"
        style={{
          color: PARCH,
          fontSize: "clamp(48px, 9vw, 84px)",
          lineHeight: 1.05,
          letterSpacing: "-0.01em",
        }}
      >
        AuraVeil
      </motion.h1>

      {/* Tagline — 400ms after artwork */}
      <motion.p
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4, ease: "easeOut" }}
        className="font-heading italic text-center mt-5 max-w-xl px-4"
        style={{
          color: PARCH,
          fontSize: "clamp(18px, 4.5vw, 22px)",
          lineHeight: 1.4,
        }}
      >
        Your spiritual identity — interpreted, illustrated, and placed in your
        hands.
      </motion.p>

      {/* Body */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.55, ease: "easeOut" }}
        className="text-center mt-6 max-w-md px-4"
        style={{
          color: `${PARCH}99`,
          fontFamily: '"DM Sans", system-ui, sans-serif',
          fontSize: "clamp(14px, 3.6vw, 15px)",
          lineHeight: 1.65,
        }}
      >
        Your birth sky and your own words become a Soul Mark: one design, drawn
        for you and nobody else, printed on the back of every card in your
        deck.
      </motion.p>

      {/* Primary Begin button */}
      <motion.button
        type="button"
        data-testid={AUTH.welcomeBeginBtn}
        onClick={onBegin}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.7, ease: "easeOut" }}
        className="btn-sacred btn-sacred-solid mt-10"
        style={{ minHeight: 48, minWidth: 200 }}
      >
        Begin
      </motion.button>

      {/* Sub-line under button */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.85, ease: "easeOut" }}
        className="mt-4 text-center"
        style={{
          color: `${PARCH}80`,
          fontFamily: '"DM Sans", system-ui, sans-serif',
          fontSize: 12,
          letterSpacing: "0.02em",
        }}
      >
        Three minutes. Free, and yours to keep.
      </motion.div>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────
// AUTH SCREEN — plays after Begin (preserved existing form)
// ─────────────────────────────────────────────────────────────
function AuthScreen({
  mode,
  setMode,
  email,
  setEmail,
  password,
  setPassword,
  name,
  setName,
  error,
  busy,
  submit,
  startGoogle,
}) {
  return (
    <motion.div
      key="auth"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5, ease: "easeInOut" }}
    >
      {/* left orbital sigils, decorative */}
      <motion.div
        aria-hidden
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 0.35, scale: 1 }}
        transition={{ duration: 3, ease: "easeInOut" }}
        className="absolute -left-40 top-1/3 w-[520px] h-[520px] rounded-full border border-[#C8A96E]/25 drift"
      />
      <motion.div
        aria-hidden
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 0.2, scale: 1 }}
        transition={{ duration: 3.5, ease: "easeInOut", delay: 0.3 }}
        className="absolute -left-20 top-1/3 w-[360px] h-[360px] rounded-full border border-[#C8A96E]/15 drift"
      />

      <div className="relative min-h-screen grid grid-cols-1 lg:grid-cols-[1.2fr_1fr]">
        <div className="flex flex-col justify-between p-8 sm:p-12 lg:p-20">
          <div>
            <BrandLogo className="h-16 sm:h-20 -ml-2" />
          </div>
          <motion.div
            data-testid={AUTH.landingHero}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: "easeInOut" }}
            className="max-w-2xl"
          >
            <div className="font-overline mb-8">A personal spiritual identity</div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[1.05] text-[#F4EFE6] font-light">
              Your soul,
              <br />
              rendered.
            </h1>
            <p className="mt-10 text-base sm:text-lg text-[#F4EFE6]/70 max-w-lg leading-relaxed font-light">
              A soul deck drawn from your natal sky, numerology, elemental
              current, and the words you offer. Not a tarot app —
              <span className="text-[#C8A96E]"> an ownable artifact of self</span>.
            </p>
          </motion.div>
          <div className="pt-16 font-overline text-[#F4EFE6]/50">
            <span className="text-[#C8A96E]">◐</span>&nbsp;&nbsp;Enter with reverence
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1.2, delay: 0.2, ease: "easeInOut" }}
          className="relative flex items-center justify-center p-8 sm:p-12"
        >
          <div className="w-full max-w-md card-frame surface-grain p-10">
            <div className="font-overline mb-8 text-center">
              {mode === "login" ? "Return to the veil" : "Begin the ceremony"}
            </div>

            <form onSubmit={submit} className="space-y-6">
              {mode === "register" && (
                <div>
                  <label className="font-overline block mb-2">Name</label>
                  <input
                    data-testid={AUTH.nameInput}
                    className="input-sacred"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="your given name"
                  />
                </div>
              )}
              <div>
                <label className="font-overline block mb-2">Email</label>
                <input
                  data-testid={AUTH.emailInput}
                  className="input-sacred"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@sanctuary.io"
                />
              </div>
              <div>
                <label className="font-overline block mb-2">Passphrase</label>
                <input
                  data-testid={AUTH.passwordInput}
                  className="input-sacred"
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="at least six letters"
                />
              </div>

              {error && (
                <div data-testid={AUTH.errorMsg} className="font-overline text-[#E9967A]">
                  {error}
                </div>
              )}

              <button
                data-testid={AUTH.submitBtn}
                type="submit"
                disabled={busy}
                className="btn-sacred btn-sacred-solid w-full"
                style={{ minHeight: 44 }}
              >
                {busy ? "Attuning…" : mode === "login" ? "Enter" : "Begin"}
              </button>
            </form>

            <div className="my-8 flex items-center gap-4">
              <div className="h-px flex-1 bg-[#C8A96E]/20" />
              <div className="font-overline text-[#F4EFE6]/50">or</div>
              <div className="h-px flex-1 bg-[#C8A96E]/20" />
            </div>

            <button
              data-testid={AUTH.googleBtn}
              onClick={startGoogle}
              className="btn-sacred w-full"
              type="button"
              style={{ minHeight: 44 }}
            >
              Continue with Google
            </button>

            <div className="mt-8 text-center">
              <button
                data-testid={AUTH.toggleModeBtn}
                type="button"
                onClick={() => setMode(mode === "login" ? "register" : "login")}
                className="font-overline text-[#F4EFE6]/60 hover:text-[#C8A96E] transition-colors"
              >
                {mode === "login" ? "New seeker — begin the ceremony" : "Return — already a seeker"}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}
